import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import DateInput from "@/components/DateInput";
import { downloadPayslip } from "@/pages/WorkerPayslips";

const money = n => (n == null ? "—" : `$${Number(n).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function Review({ run, onDone }) {
  const [err, setErr] = useState("");
  async function close() {
    if (!window.confirm(`Close the pay run ${fmtDate(run.period_start)} – ${fmtDate(run.period_end)}? This locks it, adds annual leave accrual and makes payslips.`)) return;
    try { const { data } = await api.post(`/payslip-runs/${run.id}/close`); window.alert(`Closed. ${data.payslips} payslip(s) made. Emails ${data.emails_live ? "sent" : "NOT sent (dry run — see log)"}.`); onDone(); }
    catch (e) { setErr(errText(e, "Couldn't close")); }
  }
  async function discard() { await api.delete(`/payslip-runs/${run.id}`); onDone(); }
  return (
    <div className="space-y-2" data-testid="ps-review">
      <div className="text-sm">Period {fmtDate(run.period_start)} – {fmtDate(run.period_end)} · Pay date {fmtDate(run.pay_date)} · <b>{run.status}</b> · Earnings from {run.used_detail ? "Payment Detail file" : "Hub timesheets + approved leave"}</div>
      <div className="overflow-x-auto"><table className="table text-sm"><thead><tr><th>PaySauce name</th><th>Hub worker</th><th>Gross</th><th>Hub gross</th><th>PAYE</th><th>KiwiSaver</th><th>Net</th><th>Flags</th></tr></thead>
        <tbody>{run.rows.map((r, i) => (
          <tr key={i} className={r.flags.length ? "text-amber-300" : ""} data-testid={`ps-row-${i}`}>
            <td>{r.name_raw}</td><td>{r.worker_name || <span className="text-red-400">Unmatched</span>}</td>
            <td>{money(r["Gross Pay"])}</td><td>{money(r.hub_gross)}</td><td>{money(r.PAYE)}</td><td>{money(r.KiwiSaver)}</td><td>{money(r["Net Pay"])}</td>
            <td className="text-xs">{r.flags.join("; ")}</td>
          </tr>))}</tbody></table></div>
      {err && <div className="text-red-400 text-sm">{err}</div>}
      {run.status === "Draft" && <div className="flex gap-2">
        <button className="btn-silver" onClick={close} data-testid="ps-close">Close pay run</button>
        <button className="btn-ghost text-red-400" onClick={discard} data-testid="ps-discard">Discard import</button>
      </div>}
    </div>
  );
}

export default function PaySauceImport() {
  const [f, setF] = useState({ start: "", pay: "" });
  const [summary, setSummary] = useState(null); const [detail, setDetail] = useState(null);
  const [run, setRun] = useState(null); const [runs, setRuns] = useState([]); const [slips, setSlips] = useState([]);
  const [log, setLog] = useState([]); const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  const load = () => {
    api.get("/payslip-runs").then(r => setRuns(r.data));
    api.get("/payslips").then(r => setSlips(r.data));
    api.get("/payslips/email-log").then(r => setLog(r.data));
  };
  useEffect(() => { load(); }, []);
  async function upload() {
    setErr("");
    const fd = new FormData();
    fd.append("period_start", f.start); fd.append("pay_date", f.pay); fd.append("summary", summary);
    if (detail) fd.append("detail", detail);
    try { const { data } = await api.post("/payslip-runs/import", fd); setRun(data); load(); } catch (e) { setErr(errText(e, "Import failed")); }
  }
  async function testEmail() {
    if (!window.confirm("Send a TEST payslip email to luke@lwandco.co.nz only?")) return;
    const { data } = await api.post("/payslips/test-email"); setMsg(data.ok ? `Test sent to ${data.to} ✓` : `Failed: ${data.error}`); load();
  }
  return (
    <div className="card space-y-3" data-testid="paysauce-import">
      <h3 className="text-silver">Import PaySauce pay run → payslips</h3>
      <div className="text-xs opacity-60">Upload the PaySauce “Pay Summary” CSV. Optional “Payment Detail” CSV; without it, earnings lines come from approved Hub timesheets and approved leave. No tax is calculated in the Hub — figures come from PaySauce.</div>
      <div className="flex gap-2 flex-wrap items-end">
        <label className="text-sm">Pay period start (Monday)<DateInput className="input" value={f.start} onChange={e => setF({ ...f, start: e.target.value })} data-testid="ps-start" /></label>
        <label className="text-sm">Pay date<DateInput className="input" value={f.pay} onChange={e => setF({ ...f, pay: e.target.value })} data-testid="ps-paydate" /></label>
        <label className="text-sm">Pay Summary CSV<input type="file" accept=".csv,text/csv" className="block text-xs" onChange={e => setSummary(e.target.files[0])} data-testid="ps-summary" /></label>
        <label className="text-sm">Payment Detail CSV (optional)<input type="file" accept=".csv,text/csv" className="block text-xs" onChange={e => setDetail(e.target.files[0])} data-testid="ps-detail" /></label>
        <button className="btn-silver" disabled={!f.start || !f.pay || !summary} onClick={upload} data-testid="ps-upload">Import & review</button>
      </div>
      {err && <div className="text-red-400 text-sm" data-testid="ps-error">{err}</div>}
      {run && <Review run={run} onDone={() => { setRun(null); load(); }} />}
      <div>
        <h4 className="text-sm text-silver">Imported pay runs</h4>
        <table className="table text-sm"><thead><tr><th>Period</th><th>Pay date</th><th>Status</th><th>Payslips</th><th></th></tr></thead>
          <tbody>{runs.map(r => <tr key={r.id} data-testid={`ps-run-${r.id}`}><td>{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</td><td>{fmtDate(r.pay_date)}</td><td>{r.status}</td><td>{r.payslips ?? "—"}</td>
            <td><button className="hub-link text-xs" onClick={() => api.get(`/payslip-runs/${r.id}`).then(x => setRun(x.data))} data-testid={`ps-run-open-${r.id}`}>Open</button></td></tr>)}
            {runs.length === 0 && <tr><td colSpan="5" className="opacity-50">None yet.</td></tr>}</tbody></table>
      </div>
      {slips.length > 0 && <div>
        <h4 className="text-sm text-silver">Payslips</h4>
        <table className="table text-sm"><thead><tr><th>Worker</th><th>Period</th><th>Net</th><th></th></tr></thead>
          <tbody>{slips.map(p => <tr key={p.id}><td>{p.worker_name}</td><td>{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</td><td>{money(p.totals?.net)}</td>
            <td><button className="hub-link text-xs" onClick={() => downloadPayslip(p)} data-testid={`ps-pdf-${p.id}`}>PDF</button></td></tr>)}</tbody></table>
      </div>}
      <div className="flex gap-2 items-center flex-wrap">
        <button className="btn-ghost text-sm" onClick={testEmail} data-testid="ps-test-email">Send test payslip email to luke@lwandco.co.nz</button>
        {msg && <span className="text-sm">{msg}</span>}
      </div>
      <details><summary className="text-sm cursor-pointer" data-testid="ps-email-log">Payslip email log ({log.length})</summary>
        <table className="table text-xs"><thead><tr><th>When</th><th>Worker</th><th>To</th><th>Status</th></tr></thead>
          <tbody>{log.map(x => <tr key={x.id}><td>{fmtDate(x.at)}</td><td>{x.worker_name}</td><td>{x.recipient || "—"}</td><td>{x.status}{x.error ? ` — ${x.error}` : ""}</td></tr>)}</tbody></table>
      </details>
    </div>
  );
}
